import React, { useMemo, useRef, useState } from "react";
import { useField } from "formik";
import { Dropdown } from "primereact/dropdown";

// Aman | 06/10/2026 | ITTPCS7-10143 : LazyDropdownField component for handling large datasets with pagination and search functionality.
interface LazyDropdownFieldProps {
  label: string;
  name: string;
  options: any[];
  valueField?: string;
  labelField?: string;
  className?: string;
  labelClassName?: string;
  readOnly?: boolean;
  required?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  itemSize?: number;
  onLoadMore: () => void;
  onSearch?: (query: string) => void;
  searchDebounce?: number;
}

// Dropdown backed by PrimeReact's lazy VirtualScroller so additional pages load as the user scrolls the open panel.
// Search box is a plain, self-controlled input (not PrimeReact's built-in filter) so typing is never reset
// by re-renders triggered when the paginated `options` array changes underneath it.
const LazyDropdownField: React.FC<LazyDropdownFieldProps> = ({
  label,
  name,
  options,
  valueField = "value",
  labelField = "label",
  className = "col-sm-3",
  labelClassName = "col-sm-1",
  readOnly = false,
  required = false,
  loading = false,
  hasMore = false,
  itemSize = 38,
  onLoadMore,
  onSearch,
  searchDebounce = 300,
}) => {
  const [field, meta, helpers] = useField(name);
  const [searchText, setSearchText] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const dropdownOptions = useMemo(
    () => options.map((option) => ({ label: option[labelField], value: option[valueField] })),
    [options, labelField, valueField]
  );

  const handleSearchChange = (query: string) => {
    setSearchText(query);
    if (!onSearch) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearch(query), searchDebounce);
  };

  // Handle selection of an option from the dropdown. Clears the search text and triggers a new search with an empty query if necessary.
  const handleSelect = (value: any) => {
    helpers.setValue(value);
    if (searchText) {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      setSearchText("");
      onSearch?.("");
    }
  };

  // Template for the search input box displayed at the top of the dropdown panel.
  const filterTemplate = () => (
    <div className="p-dropdown-filter-container" style={{ padding: "0.5rem" }}>
      <input
        type="text"
        className="p-inputtext p-component p-dropdown-filter"
        placeholder="Search..."
        value={searchText}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleSearchChange(e.target.value)}
        style={{ width: "100%" }}
      />
    </div>
  );

  return (
    <>
      <label htmlFor={name} className={`control-label ${labelClassName} fw-norm p-lr5`}>
        {label}
        {required && (
          <>
            &nbsp;<span style={{ color: "red" }}>#</span>
          </>
        )}
      </label>
      <div className={className}>
        <Dropdown
          id={name}
          value={field.value || ""}
          options={dropdownOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => handleSelect(e.value)}
          onBlur={() => helpers.setTouched(true)}
          disabled={readOnly}
          filter={!!onSearch}
          filterTemplate={onSearch ? filterTemplate : undefined}
          showClear
          placeholder="Choose..."
          className={meta.touched && meta.error ? "p-invalid" : undefined}
          style={{ width: "100%" }}
          virtualScrollerOptions={{
            itemSize,
            lazy: true,
            // Only show the scroll loader when there is actually more to fetch - otherwise VirtualScroller's
            // internal loading flag (set on every scroll) never gets cleared, since our `loading` prop would
            // never flip true->false to reset it (no fetch is ever dispatched when hasMore is false).
            showLoader: hasMore,
            loading,
            delay: 75,
            onLazyLoad: (event) => {
              const last = typeof event.last === "number" ? event.last : 0;
              // Fetch a bit before the actual end (not only when fully scrolled to bottom) so the next
              // page is already loading by the time the user reaches it, instead of a visible stall.
              const prefetchThreshold = Math.min(5, dropdownOptions.length);
              if (hasMore && !loading && last >= dropdownOptions.length - prefetchThreshold) {
                onLoadMore();
              }
            },
          }}
        />
        {meta.touched && meta.error && (
          <div className="help-block error-msg">{meta.error}</div>
        )}
      </div>
    </>
  );
};

export default LazyDropdownField;
